import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import styles from "./pieChart.module.scss";

interface PieChartProps {
  title?: string;
  data?: any[];
  nameKey?: string;
  valueKey?: string;
  colorKey?: string;
  showPercentage?: boolean;
  outerRadius?: number;
}

const CustomPieChart: React.FC<PieChartProps> = ({
  title = "Pie Chart",
  data = [],
  nameKey = "name",
  valueKey = "value",
  colorKey = "color",
  showPercentage = true,
  outerRadius = 120,
}) => {
  const defaultColors = [
    "#00d2aa",
    "#5b6b9e",
    "#7b8fc4",
    "#a0aed9",
    "#c5cde8",
    "#ff6b6b",
    "#ffa94d",
    "#74c0fc",
  ];

  // Transform and normalize data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      name: item[nameKey] || item.group || item.name || `Item ${index + 1}`,
      value: Number(item[valueKey] || item.value || 0),
      color: item[colorKey] || item.color || defaultColors[index % defaultColors.length],
    }));
  }, [data, nameKey, valueKey, colorKey, defaultColors]);

  // Calculate total for percentage
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percentage = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : 0;
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>
            {payload[0].name}
          </Typography>
          <Typography className={styles.tooltipValue}>
            {showPercentage ? `${percentage}%` : payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderCustomLabel = (entry: any) => {
    if (!showPercentage) return entry.value;
    const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
    return `${percentage}%`;
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.chartWrapper}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <Box className={styles.chartWrapper}>
        <Box className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => renderCustomLabel(entry)}
                outerRadius={outerRadius}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box className={styles.legendContainer}>
          {chartData.map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
            return (
              <Box key={index} className={styles.legendItem}>
                <Box className={styles.legendContent}>
                  <Box
                    className={styles.legendDot}
                    style={{ backgroundColor: entry.color }}
                  />
                  <Typography className={styles.legendText}>
                    {entry.name}
                  </Typography>
                </Box>
                <Typography className={styles.legendValue}>
                  {showPercentage ? `${percentage}%` : entry.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomPieChart;
